import { useCallback } from 'react';
import { Api } from 'src/api';
import { FormMediaObject, FormMediaValue } from 'src/components/Form/S3UploadInput/S3UploadInput.types';
// import { useFormikContext } from 'formik';
import { useFormContext } from 'react-hook-form';
import { calculateChunks, S3Uploader } from 'src/services/s3-uploader';
import { useMediaUploadStore } from 'src/lib/services/media-upload-store';
import { useShallow } from 'zustand/react/shallow';

const THROTTLE_PROGRESS_UPDATE = 100;
import { isMediaArray, isMediaCollection } from 'src/components/Form/S3UploadInput/S3UploadInput.utils';

const useUpload = () => {
  const [isUploading, addUpload, updateProgress, finishUpload, setFailed, getStatus] =
    useMediaUploadStore(
      useShallow(
        ({ isUploading, addUpload, updateProgress, finishUpload, setFailed, getStatus }) => [
          isUploading,
          addUpload,
          updateProgress,
          finishUpload,
          setFailed,
          getStatus,
        ],
      ),
    );
  // const { setValues } = useFormikContext<{ media: FormMediaValue<any> }>();
  const { setValue, getValues } = useFormContext<{ media: FormMediaValue<any> }>();

  const generateUpload = useCallback(
    async (model: string, file: File, collection_name?: string): Promise<FormMediaObject> => {
      const data = {
        mime_type: file.type,
        name: file.name,
        size: file.size,
        chunks: calculateChunks(file.size),
        collection_name: collection_name,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };

      const multiPartUpload = await Api.media.generateMultipartUpload(model, data);

      return {
        ...multiPartUpload.media,
        upload_id: multiPartUpload.upload_id,
        presigned_urls: multiPartUpload.presigned_urls,
        file,
      };
    },
    [],
  );

  const upload = useCallback(
    async (media: FormMediaObject, controller?: AbortController, onFail?: (error: any) => void) => {
      if (!media?.upload_id) {
        return;
      }

      if (isUploading(media.id)) {
        return;
      }

      addUpload({ id: media.id, name: media.file_name, uploadId: media.upload_id });

      try {
        const S3 = new S3Uploader(controller ?? getStatus(media.id)?.abortController);
        const uploadStartTime = Date.now();
        let lastUpdate = uploadStartTime;

        const uploadedData: { [chunk: number]: number } = {};

        const parts = await S3.upload(
          media.presigned_urls!,
          media.file!,
          (data: ProgressEvent, chunkIndex: number) => {
            uploadedData[chunkIndex] = data.loaded;

            // throttle progress update to avoid massive amount of store updates and re-renders at once.
            if (Date.now() - lastUpdate < THROTTLE_PROGRESS_UPDATE) {
              return;
            }

            const sum = Object.values(uploadedData).reduce((a, b) => a + b, 0);
            const percent = Math.round((sum / media.file!.size) * 100);

            // Calculate elapsed time
            const currentTime = Date.now();
            const elapsedTime = currentTime - uploadStartTime;

            // Calculate upload speed (bytes per millisecond)
            const uploadSpeed = sum / (elapsedTime / 1000);

            // Calculate remaining time in milliseconds
            const remainingBytes = media.file!.size - sum;
            const timeLeft = remainingBytes / uploadSpeed;

            updateProgress(media.id, percent, timeLeft, uploadSpeed);
            lastUpdate = Date.now();
          },
          onFail,
        );

        const completedMedia = await Api.media.completeMultipartUpload(
          media.id,
          {
            upload_id: media.upload_id!,
            parts: parts,
          },
          controller ?? getStatus(media.id)?.abortController,
        );

        // Update form media object with the completed media and clear upload_id
        // setValues((prev: { media: FormMediaValue<any> }) => { ... });
        {
          const prev = getValues() as { media: FormMediaValue<any> };
          if (isMediaArray(prev.media)) {
            const updated = (prev.media as FormMediaObject[]).map((m: FormMediaObject) =>
              m.id === media.id ? { ...completedMedia, file: media.file, upload_id: undefined } : m,
            );
            setValue('media' as any, updated as any, { shouldDirty: true, shouldValidate: true });
          } else if (isMediaCollection(prev.media)) {
            const updatedMedia: any = { ...(prev.media as any) };
            Object.keys(updatedMedia).forEach((collectionName) => {
              updatedMedia[collectionName] = updatedMedia[collectionName].map((m: FormMediaObject) =>
                m.id === media.id ? { ...completedMedia, file: media.file, upload_id: undefined } : m,
              );
            });
            setValue('media' as any, updatedMedia as any, {
              shouldDirty: true,
              shouldValidate: true,
            });
          }
        }
      } catch (e) {
        setFailed(media.id);
        throw e;
      } finally {
        finishUpload(media.id);
      }
    },
    [],
  );

  return { generateUpload, upload };
};

export { useUpload };
